import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, Space, message, Typography } from "@mimber/ui-web"
import {
  Form,
  InputField,
  PhoneNumberField,
  ZipCodeField,
  StateField,
  CountryField,
} from "@mimber/forms"
import { useUpdateOrganizationProfile } from "@mimber/client/mimber-organization"
import type { OrganizationDetailsFormProps } from "./OrganizationDetailsForm.types"
import {
  organizationDetailsSchema,
  type OrganizationDetailsFormData,
} from "./OrganizationDetailsForm.schema"

const { Paragraph } = Typography

export const OrganizationDetailsForm: React.FunctionComponent<
  OrganizationDetailsFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)

  const form = useForm<OrganizationDetailsFormData>({
    defaultValues: {
      name: organization.name || "",
      legalName: organization.legalName || "",
      industry: organization.industry || "",
      description: organization.description || "",
      phoneNumber: organization.phoneNumber || "",
      address: organization.address || "",
      city: organization.city || "",
      state: organization.state || "",
      zipCode: organization.zipCode || "",
      country: organization.country || "",
      taxId: organization.taxId || "",
    },
    resolver: zodResolver(organizationDetailsSchema),
    mode: "onBlur",
  })

  const {
    formState: { isDirty },
    reset,
  } = form

  const onSubmit = (data: OrganizationDetailsFormData) => {
    const payload = {
      name: data.name,
      legalName: data.legalName || null,
      industry: data.industry || null,
      description: data.description || null,
      phoneNumber: data.phoneNumber || null,
      address: data.address || null,
      city: data.city || null,
      state: data.state || null,
      zipCode: data.zipCode || null,
      country: data.country || null,
      taxId: data.taxId || null,
    }

    updateProfile.mutate(payload, {
      onSuccess: () => {
        message.success("Organization details updated successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to update organization details")
      },
    })
  }

  const isLoading = updateProfile.isPending

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={disabled || isLoading}
      layout="vertical"
    >
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Manage your organization's core details and contact information
      </Paragraph>

      <InputField name="name" label="Name" placeholder="Organization name" />

      <InputField
        name="legalName"
        label="Legal Name"
        placeholder="Legal entity name"
      />

      <InputField
        name="industry"
        label="Industry"
        placeholder="e.g., Technology, Healthcare, Education"
      />

      <InputField
        name="description"
        label="Description"
        placeholder="Describe your organization..."
        multiline
        rows={4}
      />

      <PhoneNumberField
        name="phoneNumber"
        label="Phone Number"
        placeholder="Phone number"
      />

      <InputField name="address" label="Address" placeholder="Street address" />

      <InputField name="city" label="City" placeholder="City" />

      <StateField name="state" label="State" />

      <ZipCodeField name="zipCode" label="Zip Code" placeholder="Zip code" />

      <CountryField name="country" label="Country" />

      <InputField name="taxId" label="Tax ID" placeholder="Tax ID" />

      <Space style={{ marginTop: 24 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={disabled || !isDirty}
          loading={isLoading}
        >
          Save Changes
        </Button>

        <Button
          onClick={() => reset()}
          disabled={disabled || !isDirty || isLoading}
        >
          Reset
        </Button>
      </Space>
    </Form>
  )
}
